import type {ReactElement} from 'react';
import {useRef} from 'react';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {openEmailCampaignSendingOptionsModal} from '@Modals/email-campaign-sending-options-modal/email-campaign-sending-options-modal';
import {EmailCampaignSendingOptionsPanelTypes} from '@Panels/email-campaign-sending-options-panel/email-campaign-sending-options-panel.types';
import styles from './email-maker-success-cta-container.module.scss';
import EmailMakerSuccessCtaMenuOptions from './components/email-maker-success-cta-menu-options/email-maker-success-cta-menu-options';

export interface EmailMakerSuccessCtaContainerProps {
  campaignId: string;
  isSchduled: boolean;
  isContentPlannerFlow?: boolean;
}

const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 531,
  },
  large: {
    minWidth: 532,
  },
};

export default function EmailMakerSuccessCtaContainer({campaignId, isSchduled, isContentPlannerFlow = false}: EmailMakerSuccessCtaContainerProps): ReactElement<any> {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);

  const getStylesFromParams = (): string => {
    if (params.small) {
      return `${styles.small}`;
    }

    return ``;
  };

  const getMenuOptions = (): ReactElement<any> => {
    return <EmailMakerSuccessCtaMenuOptions campaignId={campaignId} isScheduled={isSchduled} isContentPlannerFlow={isContentPlannerFlow} toggleButton={getMoreOptionsButton()} />;
  };

  const getPrimaryCta = (): ReactElement<any> => {
    return (
      <Button
        onClick={() => {
          openEmailCampaignSendingOptionsModal({
            campaignId,
            type: EmailCampaignSendingOptionsPanelTypes.SHARE_ON_SOCIAL_MEDIA,
          });
        }}
        customClasses={`${styles.buttonClasses}  ${getStylesFromParams()}`}
        size={Size.MEDIUM}
        icon="icon-social-media"
        iconPlacement={IconPlacement.LEFT}
        type={Type.PRIMARY}
        text={window.i18next.t('pmwjs_publish_to_the_world')}
      />
    );
  };

  const getSecondayCta = (): ReactElement<any> => {
    return (
      <Button
        onClick={() => {
          openEmailCampaignSendingOptionsModal({
            campaignId,
            type: EmailCampaignSendingOptionsPanelTypes.SHARE_AS_WEBPAGE,
          });
        }}
        customClasses={`${styles.buttonClasses} ${getStylesFromParams()}`}
        size={Size.MEDIUM}
        icon="icon-link"
        iconPlacement={IconPlacement.LEFT}
        type={Type.SECONDARY}
        text={window.i18next.t('pmwjs_share_as_webpage')}
      />
    );
  };

  const getMoreOptionsButton = (): ReactElement<any> => {
    return <Icon shape={IconShape.SQUARE} size={IconSize.SIZE_ICON_24} type={IconType.SECONDARY} icon="icon-menu-dots" />;
  };

  const getSecondaryCtas = (): ReactElement<any> => {
    if (params.small) {
      return (
        <div className={styles.mobileSecondaryCtaWrapper}>
          {getSecondayCta()} {getMenuOptions()}
        </div>
      );
    }

    return (
      <>
        {getSecondayCta()} {getMenuOptions()}
      </>
    );
  };

  return (
    <div ref={containerRef} className={styles.wrapper}>
      <div className={`${styles.ctaContainer} ${getStylesFromParams()}`}>
        {getPrimaryCta()} {getSecondaryCtas()}
      </div>
    </div>
  );
}
